/*
 * Copyright © 2018 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschützt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulässig. Dies gilt
 * insbesondere für Vervielfältigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht übergeben, ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 */

import {ChangeDetectionStrategy, Component, computed, inject, input, signal} from '@angular/core';
import {FormsModule, NgForm} from '@angular/forms';
import {ErrorService} from '@dv/kitadmin/core/errors';
import {DatepickerTextfieldComponent, DvLocalDateFormatPipe, LoadingButtonComponent} from '@dv/shared/angular';
import {ReportingControllingApi} from '@dv/shared/backend/api/reporting-controlling.api';
import {ReportingFakturaApi} from '@dv/shared/backend/api/reporting-faktura.api';
import {JaxReportTemplate} from '@dv/shared/backend/model/jax-report-template';
import type {JaxTempBlob} from '@dv/shared/backend/model/jax-temp-blob';
import {
    checkPresent,
    DvbRestUtil,
    DvbUtil,
    ParameterKinderOrte,
    ParameterKinderOrteWithGueltigkeit,
    ReportState,
} from '@dv/shared/code';
import {DownloadService} from '@dv/shared/download';
import {TranslocoDirective} from '@jsverse/transloco';
import {StateService, UIRouterGlobals} from '@uirouter/core';
import type moment from 'moment';
import type {BsDatepickerConfig} from 'ngx-bootstrap/datepicker';
import {finalize, mergeMap, Observable, switchMap, tap} from 'rxjs';
import {ReportPollingService} from '../../service/report-polling.service';
import {KinderOrteSelectionComponent} from '../kinder-orte-selection/kinder-orte-selection.component';
import {ReportTemplateSelectorComponent} from '../report-template-selector/report-template-selector.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'dv-kitas-with-gueltigkeit-report',
    imports: [
        FormsModule,
        ReportTemplateSelectorComponent,
        KinderOrteSelectionComponent,
        DatepickerTextfieldComponent,
        LoadingButtonComponent,
        DvLocalDateFormatPipe,
        TranslocoDirective,
    ],
    templateUrl: './kinder-orte-with-gueltigkeit-report.component.html',
    styles: ':host { display: block; }',
})
export class KinderOrteWithGueltigkeitReportComponent {

    public reportState = input.required<ReportState>();
    public parameterKinderOrte = input<ParameterKinderOrte>();
    public gueltigAb = input<moment.Moment>();
    public gueltigBis = input<moment.Moment>();
    public defaultGueltigAb = input<moment.Moment>();
    public defaultGueltigBis = input<moment.Moment>();
    public dateOptions = input<BsDatepickerConfig>();

    private reportingFakturaApi = inject(ReportingFakturaApi);
    private reportingControllingApi = inject(ReportingControllingApi);
    private reportPollingService = inject(ReportPollingService);
    private errorService = inject(ErrorService);
    private stateService = inject(StateService);
    private uiRouterGlobals = inject(UIRouterGlobals);
    private downloadService = inject(DownloadService);

    public formState = computed(() => {
        const paramKinderOrte = this.parameterKinderOrte();
        const gueltigAb = this.gueltigAb();
        const gueltigBis = this.gueltigBis();

        return {
            reportTemplate: signal<JaxReportTemplate | undefined>(undefined),
            kinderOrtSelection: signal({
                alleKinderOrte: paramKinderOrte?.alleKinderOrte ?? true,
                kinderOrte: paramKinderOrte?.kinderOrte ?? [],
            }),
            gueltigAb: signal(gueltigAb),
            gueltigBis: signal(gueltigBis),
        };
    });

    public isLoading = signal(false);

    public updateState(): void {
        const formState = this.formState();
        const updatedParams = Object.assign(
            {},
            this.uiRouterGlobals.params,
            {
                alleKinderOrte: formState.kinderOrtSelection().alleKinderOrte,
                kinderOrte: formState.kinderOrtSelection().kinderOrte,
                gueltigAb: formState.gueltigAb(),
                gueltigBis: formState.gueltigBis(),
            },
        );

        this.stateService.go('.', updatedParams, {reload: false});
    }

    public submitForm(form: NgForm): void {
        this.errorService.clearAll();

        const params = this.toParamsWithDefaults();
        let isValid = !!form.valid && params.isValid();
        this.errorService.handleValidationError(isValid, 'ERRORS.ERR_INCOMPLETE_FORM');

        if (isValid && ReportState.CONTROLLING.CORONA_REFUNDS === this.reportState()) {
            const validDates = params.gueltigAb!.isSame(params.gueltigBis, 'month');
            this.errorService.handleValidationError(validDates, 'ERRORS.ERR_VON_BIS_SAME_MONTH');

            isValid = validDates ? isValid : false;
        }

        if (!isValid) {
            return;
        }

        this.downloadReport(params);
    }

    private downloadReport(params: ParameterKinderOrteWithGueltigkeit): void {
        this.isLoading.set(true);

        const jaxParameterKitas = {
            alleKitas: params.alleKinderOrte,
            jahr: params.year ?? undefined,
            stichtag: DvbRestUtil.momentToLocalDate(params.stichtag) ?? undefined,
            kitaIds: params.kinderOrte.map(DvbUtil.mapToIdChecked),
        };

        const reportTemplate = this.formState().reportTemplate()?.id ?? undefined;
        const gueltigkeitWithTemplate = {
            gueltigAb: DvbRestUtil.momentToLocalDate(params.gueltigAb) ?? undefined,
            gueltigBis: DvbRestUtil.momentToLocalDate(params.gueltigBis) ?? undefined,
            reportTemplate,
        };

        const request$: Observable<JaxTempBlob> = (() => {
            switch (this.reportState()) {
                case ReportState.FAKTURA.ZAHLUNGS_EINGAENGE:
                    return this.reportingFakturaApi.getZahlungseingaenge$({
                        zahlungseingaenge: gueltigkeitWithTemplate,
                        jaxParameterKitas,
                    });
                case ReportState.FAKTURA.RECHNUNGS_GEBUEHREN:
                    return this.reportingFakturaApi.getRechnungsgebuehren$({
                        rechnungsgebuehren: gueltigkeitWithTemplate,
                        jaxParameterKitas,
                    });
                case ReportState.CONTROLLING.BETREUUNGSGUTSCHEINE:
                    return this.reportingControllingApi.getBetreuungsGutscheine$({
                        betreuungsgutscheine: gueltigkeitWithTemplate,
                        jaxParameterKitas,
                    });
                case ReportState.FAKTURA.LEISTUNGEN_FAKTURIERT:
                    return this.reportingFakturaApi.getFakturierteLeistungen$({
                        fakturiert: gueltigkeitWithTemplate,
                        jaxParameterKitas,
                    });
                case ReportState.FAKTURA.RECHNUNGS_POSITIONEN:
                    return this.reportingFakturaApi.getRechnungsPositionen$({
                        rechnungspositionen: gueltigkeitWithTemplate,
                        jaxParameterKitas,
                    }).pipe(switchMap(response => this.reportPollingService.startPollingForJaxBlob(response)));
                default:
                    throw new Error(`Report not implemented: ${JSON.stringify(this.reportState)}`);
            }
        })();

        request$.pipe(
            mergeMap(tempBlob => this.downloadService.getBlob$(checkPresent(tempBlob)).pipe(
                tap(blob => this.downloadService.downloadBlob(blob, checkPresent(tempBlob).filename)),
            )),
            finalize(() => {
                this.isLoading.set(false);
            }),
        ).subscribe();
    }

    private toParamsWithDefaults(): ParameterKinderOrteWithGueltigkeit {
        const formState = this.formState();

        return new ParameterKinderOrteWithGueltigkeit(
            formState.kinderOrtSelection().alleKinderOrte,
            formState.kinderOrtSelection().kinderOrte,
            null,
            null,
            formState.gueltigAb() ?? this.defaultGueltigAb(),
            formState.gueltigBis() ?? this.defaultGueltigBis(),
        );
    }
}
